import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getExpenses, getExpenseCategories, voidExpense, getAssetUrl } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SelectBox from "../components/UI/SelectBox";
import FloatingInput from "../components/UI/FloatingInput";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import ConfirmModal from "../components/UI/ConfirmModal";
import { notify } from "../utils/notify";
import { I18nContext, useTranslation } from "../context/I18nContext";

function money(n) {
  return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Expenses() {
  // Both an expense's Category name and an expense category's own name in
  // the filter dropdown can come back translated per the signed-in user's
  // own language (see ResolveEntityNames in entity_translation_handler.go
  // and expense_handler.go) — so both fetches below need to re-run
  // whenever languageCode changes, not just on mount — same reasoning as
  // Products.js.
  const { languageCode } = useContext(I18nContext);
  const { t } = useTranslation("accounting");
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, serverLimit: 10 });
  const [categories, setCategories] = useState([]);
  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });
  const [status, setStatus] = useState("idle");

  const currentPage = parseInt(searchParams.get("page") || "1");
  const categoryId = searchParams.get("category_id") || "";
  const dateFrom = searchParams.get("date_from") || "";
  const dateTo = searchParams.get("date_to") || "";

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchExpenses = async () => {
    try {
      const params = { page: currentPage };
      if (categoryId) params.category_id = categoryId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const response = await getExpenses(token, params);
      setData({
        items: response.data || [],
        total: response.meta?.total || 0,
        serverLimit: response.meta?.limit || 10,
      });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchExpenses(); }, [currentPage, categoryId, dateFrom, dateTo, languageCode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { getExpenseCategories(token).then(setCategories).catch(() => {}); }, [languageCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateParams = (newParams) => {
    const current = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...current, ...newParams });
  };

  const totalPages = Math.ceil(data.total / data.serverLimit) || 1;
  const categoryOptions = [{ value: "", label: t("filter_category_all_option", "All categories") }, ...categories.map((c) => ({ value: String(c.id), label: c.name }))];

  const handleVoidClick = (id) => setModalConfig({ isOpen: true, id });

  const confirmVoid = async () => {
    try {
      setStatus("loading");
      await voidExpense(token, modalConfig.id, "");
      setStatus("success");
      setTimeout(() => {
        setModalConfig({ isOpen: false, id: null });
        setStatus("idle");
      }, 1200);
      notify.success(t("notify_expense_voided", "Expense voided — its journal entry has been reversed"));
      fetchExpenses();
    } catch (err) {
      setStatus("error");
      notify.error(err.data?.error || err.message || t("notify_expense_void_failed", "Failed to void expense"));
    }
  };

  const columns = [
    { key: "expense_number", label: t("col_expense_number", "Expense #"), className: "text-gray-700 font-mono font-bold" },
    { key: "expense_date", label: t("col_date", "Date"), className: "text-gray-500", render: (row) => new Date(row.expense_date).toLocaleDateString() },
    { key: "category", label: t("col_category", "Category"), className: "text-gray-700", render: (row) => row.category?.name || `#${row.category_id}` },
    { key: "description", label: t("col_description", "Description"), className: "text-gray-500", render: (row) => row.description || "—" },
    {
      key: "payment_method",
      label: t("col_payment_method", "Payment Method"),
      render: (row) => (
        <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-gray-100 text-gray-500">
          {t(`payment_method_${row.payment_method}`, row.payment_method)}
        </span>
      ),
    },
    { key: "amount", label: t("col_amount", "Amount"), className: "text-gray-800 font-mono font-bold", render: (row) => money(row.amount) },
    {
      key: "receipt",
      label: t("col_receipt", "Receipt"),
      render: (row) => (row.receipt_url ? (
        <a href={getAssetUrl(row.receipt_url)} target="_blank" rel="noreferrer" className="text-[var(--primary-color)] underline text-xs font-bold">
          {t("btn_view_receipt", "View")}
        </a>
      ) : <span className="text-gray-300">—</span>),
    },
    {
      key: "is_voided",
      label: t("col_status", "Status"),
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${row.is_voided ? "bg-red-50 text-red-500" : "bg-green-50 text-green-600"}`}>
          {row.is_voided ? t("expense_status_voided", "Voided") : t("expense_status_active", "Active")}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={t("expense_void_confirm_title", "Void Expense")}
        message={t("expense_void_confirm_message", "This reverses the linked journal entry and marks the expense voided — it stays visible for the record. Continue?")}
        onConfirm={confirmVoid}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
        isLoading={status === "loading"}
        isSuccess={status === "success"}
        isError={status === "error"}
        confirmLabel={t("btn_void", "Void")}
        successTitle={t("void_success_title", "Voided!")}
        successMessage={t("notify_expense_voided", "Expense voided — its journal entry has been reversed")}
      />

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Expenses size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("expenses_page_title", "Expenses")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("expenses_page_subtitle", "Every recorded expense auto-posts its own journal entry")}</p>
          </div>
        </div>

        <Button variant="primary" icon={ICONS.Plus} onClick={() => navigate("/add-expense")} className="w-full sm:w-auto shrink-0">
          {t("btn_add_expense", "Record Expense")}
        </Button>
      </PageHeader>

      <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SelectBox label={t("filter_category_label", "Category")} options={categoryOptions} value={categoryId} onChange={(val) => updateParams({ category_id: val, page: 1 })} />
        <FloatingInput label={t("filter_date_from_label", "From")} type="date" value={dateFrom} onChange={(e) => updateParams({ date_from: e.target.value, page: 1 })} />
        <FloatingInput label={t("filter_date_to_label", "To")} type="date" value={dateTo} onChange={(e) => updateParams({ date_to: e.target.value, page: 1 })} />
      </div>

      <DataTable
        columns={columns}
        data={data.items}
        sortConfig={{ key: "expense_date", direction: "desc" }}
        onSort={() => {}}
        renderActions={(row, isMobile) => (
          !row.is_voided && (
            <Button variant="danger" size="sm" icon={ICONS.Close} onClick={() => handleVoidClick(row.id)}>{isMobile && ` ${t("btn_void", "Void")}`}</Button>
          )
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">{row.expense_number}</span>
            <h3 className="font-bold text-gray-800">{money(row.amount)}</h3>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => updateParams({ page: p }),
          totalItems: data.total,
        }}
      />
    </div>
  );
}
